import { AxiosError, AxiosInstance, AxiosProgressEvent, create } from "axios"
import { config } from "~/config"
import { ErrorResponse } from "~/types"
import { AuthEvents, UNAUTHORIZED_EVENT, ACCESS_DENIED_EVENT, NavigationEvents } from "~/utils"

type RequestConfig<QueryParams> = {
  url?: string
  params?: QueryParams
  instance?: AxiosInstance
}

type HttpMutationMethod = "post" | "put" | "patch"

type MutationConfig<PayloadType, QueryParams> = RequestConfig<QueryParams> & {
  payload?: PayloadType
  ContentType?: string
  setProgress?: (value: number) => void
  signal?: AbortSignal
}

const axiosInstance = create({
  baseURL: config.vastApiBaseUrl,
  withCredentials: true,
})

axiosInstance.defaults.headers["X-Client-Id"] = config.clientId

const axiosGet = async <PromiseType = any, QueryParams = Record<any, any>>({
  url = "",
  params,
  instance = axiosInstance,
}: RequestConfig<QueryParams>): Promise<PromiseType> => {
  try {
    return await instance.get(url, { params })
  } catch (error: unknown) {
    // log original request error
    console.error("Axios GET request error:", error)

    const axiosError = error as AxiosError<{ message: string }>

    const errorResponse = new Error(
      axiosError.response?.data?.message || axiosError.message || "An error occurred"
    ) as ErrorResponse
    errorResponse.statusCode = axiosError.response?.status || -1

    throw errorResponse
  }
}

const axiosMutate = async <PayloadType = any, PromiseType = any, QueryParams = Record<any, any>>(
  method: HttpMutationMethod,
  {
    url = "",
    params,
    instance = axiosInstance,
    payload,
    ContentType = "application/json",
    setProgress,
    signal,
  }: MutationConfig<PayloadType, QueryParams>
): Promise<PromiseType> => {
  try {
    return await instance[method](url, payload, {
      params,
      headers: {
        "Content-Type": ContentType,
      },
      signal,
      // Updates progress percentage during file upload.
      onUploadProgress: ({ lengthComputable, loaded, total }: AxiosProgressEvent) => {
        if (lengthComputable && total) {
          setProgress?.(Math.round((loaded / total) * 100))
        }
      },
    })
  } catch (error: unknown) {
    // log original request error
    console.error(`Axios ${method.toUpperCase()} request error:`, error)

    const axiosError = error as AxiosError<{ message: string }>

    const errorResponse = new Error(
      axiosError.response?.data?.message || axiosError.message || "An error occurred"
    ) as ErrorResponse
    errorResponse.statusCode = axiosError.response?.status || -1

    throw errorResponse
  }
}

const axiosPost = <PayloadType = any, PromiseType = any, QueryParams = Record<any, any>>(
  config: MutationConfig<PayloadType, QueryParams>
): Promise<PromiseType> => axiosMutate("post", config)

const axiosPut = <PayloadType = any, PromiseType = any, QueryParams = Record<any, any>>(
  config: MutationConfig<PayloadType, QueryParams>
): Promise<PromiseType> => axiosMutate("put", config)

export { axiosGet, axiosPost, axiosPut, axiosInstance }

type RequestErrorHandlerOptions = {
  skipAuthErrors?: boolean
}

type InterceptedError = any

export const handleRequestError = (
  error: InterceptedError,
  options?: RequestErrorHandlerOptions
): Promise<InterceptedError> => {
  const { skipAuthErrors = false } = options || {}

  // Handle errors globally
  if (error.response) {
    if (error.response.status === 401 && !skipAuthErrors) {
      AuthEvents.emit(UNAUTHORIZED_EVENT)
    } else if (error.response.status === 403 && !skipAuthErrors) {
      NavigationEvents.emit(ACCESS_DENIED_EVENT)
    }
  } else if (error.request) {
    // No response was received from the server
    console.error("No response received: Please check your network")
  } else {
    // Error occurred in setting up the request
    console.error("Request error:", error.message)
  }

  // Return a rejected promise to propagate the error
  return Promise.reject(error)
}

//  Global state to track the current error interceptor
let errorInterceptorId: number | null = null

export const setupResponseErrorInterceptor = (): void => {
  // If an interceptor already set up. Remove (eject) it first.
  if (errorInterceptorId !== null) {
    axiosInstance.interceptors.response.eject(errorInterceptorId)
    errorInterceptorId = null
    console.warn("Previous axios error interceptor ejected")
  }

  errorInterceptorId = axiosInstance.interceptors.response.use((response) => response, handleRequestError)
}
